import { ParsedUrlQuery } from "querystring";
import { GetServerSidePropsContext } from "next";
import getConfig from "next/config";

/**
 * Finds and returns the key in the object that matches the given name ignoring case.
 */
const findKeyIgnoreCase = (obj: Record<string, any>, name: string): string|null => {
    const upperKey = name?.toUpperCase() ?? "";
    if (!upperKey) return null;

    return Object.keys(obj).filter((key) => key.toUpperCase() === upperKey).shift() ?? null;
};

/**
 * Checks if a value is true or the string value of true.
 */
const isTruthy = (value: any = false) => value?.toString().toUpperCase() === "TRUE";

/**
 * Take a name and determine if the feature flags
 * is activated based on environment variable.
 */
const isFeatureFlagActive = (name: string, query: ParsedUrlQuery, config: Record<string, string|boolean|number>) => {
    // all keys should be upper case
    const queryKey = findKeyIgnoreCase(query, name);
    const configKey = findKeyIgnoreCase(config, name);

    // Did we pass the key in the URL as true?
    if (isTruthy(query[queryKey])) return true;

    // Did we pass the key in the URL as false?
    if (query[queryKey] && !isTruthy(query[queryKey])) return false;

    // Did we enable the key in the config?
    if (isTruthy(config[configKey])) return true;

    return false;
};

/**
 * Returns a list of all existing flags in the config.
 */
const getFeatureFlags = () => {
    const { publicRuntimeConfig } = getConfig();
    return Object.keys(publicRuntimeConfig).reduce((prev, curr) => {
        if (/FEATURE_FLAG/i.test(curr)) {
            prev.push(curr);
        }
        return prev;
    }, []);
};

/**
 * Returns all active flags in the config and/or query params.
 */
const getActiveFeatureFlags = (context: GetServerSidePropsContext) => {
    const { query } = context;
    const { publicRuntimeConfig } = getConfig();
    const featureFlagsInQuery = getFeatureFlags().reduce((featureFlags, featureFlag) => ({
        ...featureFlags,
        [featureFlag]: isFeatureFlagActive(featureFlag, query, publicRuntimeConfig)
    }), {});
    return featureFlagsInQuery;
};

export {
    isFeatureFlagActive,
    getFeatureFlags,
    getActiveFeatureFlags
};
